import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { People, Search, Style } from '@local/client-contracts';
import { isMac } from '@local/common-web';
import { KeyName, getModifiers } from '@local/ts-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventInfo, EventInfoResource, EventsService } from '@shared/services';
import { AppService } from '@shared/services/app.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { windowSizeObserver } from '@shared/utils';
import { getWidthBreakpointScreen } from '@shared/utils/break-point.utils';
import { NgScrollbar } from 'ngx-scrollbar';
import { BehaviorSubject, ReplaySubject, firstValueFrom } from 'rxjs';
import { ComponentFocused } from 'src/app/bar/models/component-focused.model';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { ResultContextMenuItem, SearchResults, TelemetryTrigger } from 'src/app/bar/views/results/models/results-types';
import { PreviewType } from 'src/app/bar/views/results/models/view-filters';
import { ScrollService } from 'src/app/bar/views/results/services/scroll.service';
import { PreviewMode } from '../../../model/preview-mode';
import { fade } from '../../fade.animations';
import { PreviewPeopleResults } from '../../models/preview-people-results';
import { PeopleService } from '../../services/people.service';
import { PreviewResultItemComponent } from './preview-result-item/preview-result-item.component';
export type DisplayItem = Search.ResultResourceItem & { displayIndex?: number };
@UntilDestroy()
@Component({
  selector: 'preview-results',
  templateUrl: './preview-results.component.html',
  styleUrls: ['./preview-results.component.scss'],
  animations: [fade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewResultsComponent implements OnInit, OnDestroy {
  private readonly COMPONENT_INDEX = 4;
  private _smallWidthPreview = true;
  isLauncher: boolean;
  @Input() set results(value: PreviewPeopleResults) {
    this.isLoading = true;
    this.isEmpty = false;
    this.loadingRows = this.size === 'popup' ? 4 : 20;
    if (value) {
      this.isLoading = false;
      if (value.totalResults === 0) {
        this.isEmpty = true;
      } else {
        this.countItems = value?.items?.length;
        this.results$.next(value);
        this.resultsImpressionEvent();
      }
    }
    setTimeout(() => {
      this.updateScrollHeight();
      this.refreshScrollService(value);
    }, 50);
  }
  @Input() set componentFocused({ compIndex, direction }: ComponentFocused) {
    if (compIndex === this.COMPONENT_INDEX) {
      if (this.keyHandlerId) return;
      this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
        this.handleKeys(keys, event);
      }, 6);
      this.selectedIndex = 0;
      return;
    }
    this.unregisterKeyHandler();
  }
  @Input() userName: string;
  @Input() userEmail: string;
  @Input() size: PreviewMode;
  @Input() telemetrySearch: Partial<EventInfo>;
  @Input() set smallWidthPreview(value: boolean) {
    this._smallWidthPreview = value;
    this.updateScrollHeight();
  }
  get smallWidthPreview(): boolean {
    return this._smallWidthPreview;
  }
  @Input() previewPopupLarge: boolean;
  @Output() componentFocusedExit: EventEmitter<string> = new EventEmitter<string>();
  @Output() readyForFocus: EventEmitter<number> = new EventEmitter<number>();
  private keyHandlerId: string;
  isEmpty: boolean;
  isLoading: boolean;
  loadingRows: number;
  countItems = 0;
  private currentPaging = false;
  results$: ReplaySubject<PreviewPeopleResults> = new ReplaySubject(1);
  scrollHeight: string;
  private windowSize$ = windowSizeObserver();
  private scrollService: ScrollService<DisplayItem>;
  existScroll: BehaviorSubject<boolean> = new BehaviorSubject(true);
  @ViewChild(CdkVirtualScrollViewport) public scrollViewport: CdkVirtualScrollViewport;
  @ViewChild('scroll_recent_activities') public scrollElement: ElementRef;
  @ViewChild(NgScrollbar) public scrollbarRef: NgScrollbar;
  @ViewChildren('preview_result_item', { read: ElementRef }) private resultElementsRef: QueryList<ElementRef>;
  @ViewChildren('preview_result_item') resultsItems: QueryList<PreviewResultItemComponent>;
  isRelevantPeople: boolean;
  _previewType: PreviewType;
  @Input() set previewType(v: PreviewType) {
    this._previewType = v;
    this.isRelevantPeople = v === 'relevant-people';
    this.cdr.markForCheck();
  }

  selectedIndex: number;
  readonly emptyStateIcon: Style.Icon = {
    lightUrl: './assets/bar/empty-state/empty-state-light.svg',
    darkUrl: './assets/bar/empty-state/empty-state-dark.svg',
  };

  constructor(
    private peopleService: PeopleService,
    private eventService: EventsService,
    private cdr: ChangeDetectorRef,
    protected ngZone: NgZone,
    private keyboardService: KeyboardService,
    private appService: AppService,
    private resultCommandService: ResultCommandService,
    private resultsService: ResultsService
  ) {
    this.appService.windowStyle$.pipe(untilDestroyed(this)).subscribe((style) => (this.isLauncher = style != 'standard'));
  }

  ngOnInit(): void {
    this.windowSize$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateScrollHeight();
    });
    this.initScroll();
    if (!this.isEmpty) {
      this.readyForFocus.emit(this.COMPONENT_INDEX);
    }
  }

  ngOnDestroy(): void {
    this.scrollService?.destroy();
    this.unregisterKeyHandler();
  }

  unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.selectedIndex = -1;
    this.keyHandlerId = null;
  }

  private initScroll() {
    this.scrollService = new ScrollService(this.ngZone);
    this.scrollService.scrollToStart();
  }

  private refreshScrollService(value: PreviewPeopleResults) {
    const items = value?.items;
    if (!items || !this.scrollService) {
      return;
    }
    this.scrollService.items = items.map((item, index) => <DisplayItem>{ ...item, displayIndex: index });
    this.scrollService.itemHeights = Array(value.items.length).fill(53);
    this.scrollService.init(this.scrollbarRef, this.resultElementsRef, this.scrollViewport);
    if (!this.scrollViewport?.elementRef?.nativeElement) {
      this.scrollService?.destroy();
    }
  }

  private async resultsImpressionEvent() {
    const results = await firstValueFrom(this.results$);
    const resources: EventInfoResource[] = results.items?.map((r, i) => ({
      position: i,
      category: '',
      appId: r.resource?.appId,
      linkId: r.resource?.linkId,
      id: r.resource?.id,
      list: 'people_activities',
      type: r.resource?.type,
    }));
    this.eventService.event('results.impression', {
      location: { title: 'people' },
      search: this.telemetrySearch?.search,
      resources,
    });
  }

  private updateScrollHeight() {
    this.scrollHeight = `${this.calcResultsHeight()}px`;
    this.cdr.markForCheck();
  }

  private calcResultsHeight(): number {
    if (this.previewPopupLarge) return 340;
    const scrollElementHeight = window.innerHeight - this.scrollElement?.nativeElement?.getBoundingClientRect()?.top;
    if (!scrollElementHeight) {
      return 0;
    }
    let marginElement: number;
    const marginScroll = 5;
    let emptyStateHeight = 130;
    if (this.size === 'popup') {
      const popupElement = document.querySelectorAll('people-preview');
      const hierarchyElement = document.querySelectorAll('preview-hierarchy');
      const detailsElement = document.querySelectorAll('preview-details');
      const marginElement = 148;
      const minHeight = 208;
      const popupElementHeight = popupElement[popupElement.length - 1]?.getBoundingClientRect()?.height || this.peopleService.popupSize;
      const hierarchyElementHeight = hierarchyElement[hierarchyElement.length - 1]?.getBoundingClientRect()?.height || 0;
      const detailsElementHeight = detailsElement[detailsElement.length - 1]?.getBoundingClientRect()?.height || 0;
      const height = popupElementHeight - Math.max(hierarchyElementHeight || detailsElementHeight) - marginElement - marginScroll;
      if (this.countItems === 0) {
        return Math.max(emptyStateHeight, height);
      }
      return Math.max(height, minHeight);
    }
    marginElement = 52;
    const breakpointScreen = getWidthBreakpointScreen();
    if (breakpointScreen === 'large' || breakpointScreen === 'small') {
      const itemHeight = 53;
      const itemsHeight = this.countItems * itemHeight;
      if (!this.countItems || scrollElementHeight - marginElement + marginScroll <= itemsHeight || this.isEmpty == true) {
        return scrollElementHeight - marginElement + marginScroll;
      }
      this.existScroll.next(false);
      return itemsHeight + marginScroll;
    }
    emptyStateHeight = 210;
    marginElement = 15;
    if (this.countItems === 0) {
      return Math.max(emptyStateHeight, scrollElementHeight - marginElement);
    }
    return scrollElementHeight - marginElement;
  }

  public handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.resultsItems.toArray().length) return;
    const selectedItem = this.getItem();

    if (selectedItem) {
      if (isMac()) keys = keys.map((k) => (k = k === 'control' ? 'command' : k));
      const matchingIdx = selectedItem.contextMenuItems.findIndex(
        (item) => item?.shortcut && item?.shortcut?.length === keys.length && item.shortcut.every((key) => keys.includes(key))
      );

      if (matchingIdx !== -1) {
        this.emitContextMenuCommand(selectedItem.item.model, selectedItem.contextMenuItems[matchingIdx], 'context_menu_keyboard');
        setTimeout(() => {
          this.cdr.markForCheck();
          selectedItem.updateView$.next(true);
        }, 100);
        event.stopPropagation();
        return;
      }

      const modifiers = getModifiers(keys);
      switch (keys[0]) {
        case 'tab':
          if (modifiers[0] === 'shift') {
            this.componentFocusedExit.emit('prev');
            event.preventDefault();
            return;
          }
          this.componentFocusedExit.emit('next');
          return;

        case 'ArrowDown':
          this.updateSelectedIndex(1, event);
          return;

        case 'ArrowUp': {
          this.updateSelectedIndex(-1, event);
        }
      }
    }
  }

  updateSelectedIndex(value: number, event: CustomKeyboardEvent) {
    if (this.selectedIndex + value < 0 || this.selectedIndex + value >= this.resultsItems.length) {
      event.stopPropagation();
      return;
    }
    if (value === 0) this.selectedIndex = 0;
    else this.selectedIndex += value;
    this.checkIfScrollNeeded();
    event.stopPropagation();
    this.cdr.markForCheck();
  }

  async checkIfScrollNeeded() {
    const results = await firstValueFrom(this.results$);
    const newIdx = this.scrollService.scrollIfNeeded(this.selectedIndex, results.items);
    if (typeof newIdx === 'number') this.selectedIndex = newIdx;
  }

  private emitContextMenuCommand(model: Search.ResultResourceItem, item: ResultContextMenuItem, trigger: TelemetryTrigger): void {
    const label = item.id;
    const searchId = this.telemetrySearch?.search?.searchId;
    if (label === 'favorite') {
      model.isFavorite = model.isFavorite;
      this.resultCommandService.toggleFavorite(model, this.eventResources(model), trigger, searchId);
      this.cdr.markForCheck();
      return;
    }
    this.resultCommandService.executeCommand(item.command, { item: model, searchId }, 'person', {
      target: trigger,
      location: { title: this.peopleService.currentLocation },
      label,
      resources: this.eventResources(model),
    });
    this.eventService.event('context_menu.open_from_preview', {
      label,
      location: { title: this.peopleService.currentLocation },
      resources: this.eventResources(model),
    });
  }

  private eventResources(model: Search.ResultResourceItem) {
    return [
      {
        id: model?.id,
        list: 'people_view',
        appId: model?.link?.appId,
        type: model?.resource?.type,
        position: this.selectedIndex,
        linkId: model?.link?.id,
      },
    ];
  }

  private getItem(): PreviewResultItemComponent {
    return this.resultsItems.toArray().find((i) => i.index === this.selectedIndex);
  }
}
